import React from "react";
import { useTranslation } from "react-i18next";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import BaseInput from "@reusables/BaseInput";
import { Controller, useFormContext } from "react-hook-form";
import { ProductType } from "@components/Dashboard/pages/Products/components/CreationEditingLayout/types";
import { useAppSelector } from "@redux/hooks";
import { useAbility } from "@casl/react";
import { AbilityContext } from "@/casl.config";
import GrossVatPriceField from "./GrossVatPriceField";
import BaseTooltip from "@reusables/BaseTooltip";
import { AlertCircle } from "lucide-react";

export default function Prices({ editing }: { editing: boolean }) {
    const { t } = useTranslation("", { keyPrefix: "products.general" });

    const baseCurrency = useAppSelector((state) => state.auth.user?.company?.currency);

    const { control } = useFormContext<ProductType>();

    const ability = useAbility(AbilityContext);

    const canEditSalePrice = !editing || ability.can("price.edit", "product");
    const priceTitle = (
        <div className="flex items-center gap-[8px]">
            <div className="text-xl text-accent">{t("categories.prices")}</div>
            <BaseTooltip title={`${t("fields.dynamicPricePrompt")}`} placement="right">
                <AlertCircle size={18} className="text-[#686868]" />
            </BaseTooltip>
        </div>
    );

    return (
        <div className={"space-y-[40px]"}>
            <BaseInputsGrid title={priceTitle}>
                {!editing && (
                    <Controller
                        name="prices.purchase"
                        control={control}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t("fields.purchasePrice") + " *"}
                                placeholder="0.00"
                                type="number"
                                sx={{
                                    "& .MuiInputBase-input": {
                                        paddingRight: "25px",
                                    },
                                }}
                                icon={{
                                    right: {
                                        el: (
                                            <span className={"text-purple-500"}>
                                                {baseCurrency?.symbol}
                                            </span>
                                        ),
                                        offset: "10px",
                                    },
                                }}
                            />
                        )}
                    />
                )}

                {canEditSalePrice && (
                    <div className="space-y-[4px]">
                        <Controller
                            name="prices.sale"
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseInput
                                    {...field}
                                    error={fieldState.error}
                                    label={t("fields.sellingPrice") + " *"}
                                    placeholder="0.00"
                                    type="number"
                                    sx={{
                                        "& .MuiInputBase-input": {
                                            paddingRight: "25px",
                                        },
                                    }}
                                    icon={{
                                        right: {
                                            el: (
                                                <span className={"text-purple-500"}>
                                                    {baseCurrency?.symbol}
                                                </span>
                                            ),
                                            offset: "10px",
                                        },
                                    }}
                                />
                            )}
                        />

                        <GrossVatPriceField />
                    </div>
                )}

                <Controller
                    name="prices.list_price"
                    control={control}
                    render={({ field, fieldState }) => (
                        <BaseInput
                            {...field}
                            error={fieldState.error}
                            label={t("fields.listPrice")}
                            placeholder="0.00"
                            type="number"
                            sx={{
                                "& .MuiInputBase-input": {
                                    paddingRight: "25px",
                                },
                            }}
                            icon={{
                                right: {
                                    el: (
                                        <span className={"text-purple-500"}>
                                            {baseCurrency?.symbol}
                                        </span>
                                    ),
                                    offset: "10px",
                                },
                            }}
                        />
                    )}
                />

                <Controller
                    name="prices.extra_cost"
                    control={control}
                    render={({ field, fieldState }) => (
                        <BaseInput
                            {...field}
                            error={fieldState.error}
                            label={t("fields.extraCost")}
                            placeholder="0.00"
                            type="number"
                            sx={{
                                "& .MuiInputBase-input": {
                                    paddingRight: "25px",
                                },
                            }}
                            icon={{
                                right: {
                                    el: (
                                        <span className={"text-purple-500"}>
                                            {baseCurrency?.symbol}
                                        </span>
                                    ),
                                    offset: "10px",
                                },
                            }}
                        />
                    )}
                />
            </BaseInputsGrid>
        </div>
    );
}
